import { formatDate } from '@experience/datetime-utils';
import { formatTripsterMoney } from '@experience/money-utils';
import { choosePlural, formatDurationInDays, formatDurationInHours } from '@experience/shared';

import type { Order } from '@/shared/api/generated/api';
import type { Currency } from '@/shared/models/experience-price';

const HOURS_IN_DAY = 24;

const isAudioguideOrder = (order: Order): boolean =>
  order.experience?.exp_format === 'audioguide' || order.experience?.type === 'independent_walk';

export const formatOrderEventDate = (order: Order): string | undefined => {
  const date = order.event?.date;

  if (!date) {
    return undefined;
  }

  // Сокращения месяцев без точки в конце, как в exp-card (ExpDays): «июн», «июл» вместо «июнь», «июль».
  const day = formatDate(date, 'd');
  const month = formatDate(date, 'MMM').replace(/\.$/, '');
  const weekday = formatDate(date, 'EEEEEE');

  return `${day} ${month}, ${weekday}`;
};

export const formatOrderParticipants = (order: Order): string | undefined => {
  const count = order.persons_count;

  if (!count) {
    return undefined;
  }

  return `${String(count)} ${choosePlural(count, ['участник', 'участника', 'участников'])}`;
};

export const formatOrderDuration = (order: Order): string | undefined => {
  const duration = order.event?.duration ?? order.experience?.duration;

  if (duration == null) {
    return undefined;
  }

  const isMultiday = order.experience?.type === 'tour' || duration >= HOURS_IN_DAY;
  const formatted = isMultiday ? formatDurationInDays(duration) : formatDurationInHours(duration);

  return formatted || undefined;
};

export const formatOrderPrice = (order: Order): string | undefined => {
  const value = order.price?.value;
  const currency = order.price?.currency as Currency | undefined;

  if (value == null || !currency) {
    return undefined;
  }

  return formatTripsterMoney(value, currency);
};

export const formatOrderPrimaryMeta = (order: Order): string | undefined => {
  // У аудиогида не показываем дату и участников — только длительность и стоимость (в secondary).
  if (isAudioguideOrder(order)) {
    return undefined;
  }

  const parts = [formatOrderEventDate(order), formatOrderParticipants(order)].filter(Boolean);

  return parts.length ? parts.join(' · ') : undefined;
};

export const formatOrderSecondaryMeta = (order: Order): string | undefined => {
  const parts = [formatOrderDuration(order), formatOrderPrice(order)].filter(Boolean);

  return parts.length ? parts.join(' · ') : undefined;
};
